import React, {useEffect, useState} from 'react';
import {Dimensions, StyleSheet, View} from 'react-native';
import {useThemeColors} from '../HelperClasses/ThemeContext';

const getViewportWidth = () =>
  typeof window !== 'undefined' && window.innerWidth
    ? window.innerWidth
    : Dimensions.get('window').width;

const ListingsHeaderV2 = ({
  titleArea,
  viewControls,
  tableControls,
  searchControl,
  actionControl,
  simple = false,
  containerStyle,
}) => {
  const [isMobile, setIsMobile] = useState(getViewportWidth() < 900);
  const colors = useThemeColors();

  useEffect(() => {
    let mounted = true;
    const updateLayout = () => {
      if (mounted) {
        setIsMobile(getViewportWidth() < 900);
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateLayout);
    }
    const dimensionSubscription = Dimensions.addEventListener
      ? Dimensions.addEventListener('change', updateLayout)
      : null;
    return () => {
      mounted = false;
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateLayout);
      }
      if (dimensionSubscription && dimensionSubscription.remove) {
        dimensionSubscription.remove();
      }
    };
  }, []);

  const headerStyle = {
    ...styles.header,
    backgroundColor: 'transparent',
    borderBottomColor: colors.borderColor,
  };

  return (
  <View style={[headerStyle, containerStyle]}>
    {simple ? (
      <View style={styles.simpleRow}>
        <View
          style={[
            styles.simpleTitleArea,
            !viewControls && !searchControl && styles.simpleTitleAreaFlexible,
          ]}>
          {titleArea}
        </View>
        {viewControls && (
          <View style={[styles.simpleViewControls, styles.simpleViewControlsPresent]}>
            {viewControls}
          </View>
        )}
        {searchControl && <View style={styles.simpleSearchArea}>{searchControl}</View>}
        <View style={styles.simpleTableControls}>{tableControls}</View>
        <View style={styles.simpleActionArea}>{actionControl}</View>
      </View>
    ) : isMobile ? (
      <>
        <View style={styles.mobileTopRow}>
          <View style={[styles.titleArea, styles.mobileTitleArea]}>{titleArea}</View>
          <View style={styles.mobileAction}>{actionControl}</View>
        </View>
        <View style={styles.mobileBottomRow}>
          <View style={styles.viewControls}>{viewControls}</View>
          <View style={styles.tableControls}>{tableControls}</View>
          <View style={styles.mobileSearchArea}>{searchControl}</View>
        </View>
      </>
    ) : (
      <View style={styles.row}>
        <View style={styles.titleArea}>{titleArea}</View>
        <View style={styles.viewControls}>{viewControls}</View>
        <View style={styles.tableControls}>{tableControls}</View>
        <View style={styles.searchArea}>
          <View style={styles.searchInputArea}>{searchControl}</View>
          <View style={styles.actionArea}>{actionControl}</View>
        </View>
      </View>
    )}
  </View>
  );
};

const styles = StyleSheet.create({
  header: {
    width: '100%',
    position: 'relative',
    // Keep hover content above the listings table below it.
    zIndex: 10000,
    overflow: 'visible',
    borderBottomWidth: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 56,
    paddingHorizontal: 24,
    paddingVertical: 8,
    flexWrap: 'nowrap',
    position: 'relative',
    zIndex: 10000,
    overflow: 'visible',
  },
  titleArea: {
    width: 250,
    marginRight: 16,
    flexGrow: 0,
    flexShrink: 0,
  },
  simpleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    alignSelf: 'stretch',
    minHeight: 56,
    paddingHorizontal: 24,
    paddingVertical: 8,
    boxSizing: 'border-box',
    flexWrap: 'wrap',
  },
  simpleTitleArea: {
    width: 250,
    flexBasis: 250,
    flexGrow: 0,
    flexShrink: 0,
    minHeight: 40,
    marginRight: 16,
  },
  simpleTitleAreaFlexible: {
    width: undefined,
    flexBasis: undefined,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 220,
  },
  simpleActionArea: {
    flexShrink: 0,
    marginLeft: 'auto',
    minHeight: 40,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  simpleTableControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    marginRight: 16,
  },
  simpleViewControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 0,
    marginRight: 16,
  },
  simpleViewControlsPresent: {
    width: 132,
  },
  simpleSearchArea: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 260,
    minWidth: 220,
    marginRight: 16,
  },
  mobileTitleArea: {
    width: undefined,
    flexGrow: 1,
  },
  searchArea: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 360,
    minWidth: 260,
    maxWidth: 'none',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
  },
  searchInputArea: {
    flex: 1,
    minWidth: 0,
    marginRight: 10,
  },
  actionArea: {
    flexShrink: 0,
  },
  mobileTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingTop: 10,
    paddingBottom: 10,
    position: 'relative',
    zIndex: 20,
  },
  mobileBottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    paddingHorizontal: 24,
    paddingBottom: 10,
    position: 'relative',
    zIndex: 10,
  },
  mobileAction: {
    position: 'relative',
    zIndex: 30,
  },
  mobileSearchArea: {
    flex: 1,
    minWidth: 240,
    marginTop: 10,
  },
  viewControls: {
    flexShrink: 0,
    marginRight: 16,
  },
  tableControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    flexShrink: 0,
    marginRight: 16,
    position: 'relative',
    zIndex: 10000,
    overflow: 'visible',
  },
});

export default ListingsHeaderV2;
